import React, {useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {DEFAULT_TEST_CONFIG, DescriptorTestRequest, DescriptorTestState} from '../descriptor-test-types';
import occurrenceReportDescriptorService from '../../../../../../services/occurrence-report-descriptor.service';
import {toast} from 'react-toastify';

interface DescriptorTestProps {
    descriptorId: number;
    isDisabled: boolean;
    hasUnsavedChanges?: boolean;
}

/**
 * Component for testing descriptor mappings against sample report text
 */
export const DescriptorTest: React.FC<DescriptorTestProps> = ({
    descriptorId, isDisabled, hasUnsavedChanges = false
}) => {
    const [testState, setTestState] = useState<DescriptorTestState>({
        testContent: '',
        isLoading: false,
        error: null,
        results: null,
        includeExplanations: DEFAULT_TEST_CONFIG.includeExplanations,
        confidenceThreshold: DEFAULT_TEST_CONFIG.confidenceThreshold
    });

    // View mode state: 'edit' for textarea, 'highlight' for highlighted view
    const [viewMode, setViewMode] = useState<'edit' | 'highlight'>('edit');
    const [selectedTermId, setSelectedTermId] = useState<number | null>(null);

    const handleContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setTestState(prev => ({
            ...prev, testContent: event.target.value, error: null
        }));
    };

    const handleThresholdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(event.target.value);
        if (!isNaN(value) && value >= 0 && value <= 1) {
            setTestState(prev => ({
                ...prev, confidenceThreshold: value
            }));
        }
    };

    const handleExplanationsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setTestState(prev => ({
            ...prev, includeExplanations: event.target.checked
        }));
    };

    const handleTest = async (): Promise<void> => {
        // Validation
        if (!testState.testContent.trim()) {
            setTestState(prev => ({
                ...prev, error: 'Please enter report content to test'
            }));
            return;
        }

        setTestState(prev => ({...prev, isLoading: true, error: null}));

        try {
            const request: DescriptorTestRequest = {
                reportContent: testState.testContent.trim(),
                includeExplanations: testState.includeExplanations,
                confidenceThreshold: testState.confidenceThreshold
            };

            const response = await occurrenceReportDescriptorService.testDescriptorMapping(descriptorId, request);

            setTestState(prev => ({
                ...prev, isLoading: false, results: response, error: null
            }));

            // Switch to highlight view mode after successful test
            setViewMode('highlight');

            // Show success toast
            const processingTime = response.processingTimeMs ? (response.processingTimeMs / 1000).toFixed(1) : '?';
            toast.success(`Mapping test completed in ${processingTime}s`);

        } catch (error: any) {
            setTestState(prev => ({
                ...prev, isLoading: false, error: error.message || 'Failed to test descriptor mapping'
            }));
            toast.error('Mapping test failed');
        }
    };

    const handleClear = (): void => {
        setTestState(prev => ({
            ...prev, testContent: '', error: null, results: null
        }));
        setViewMode('edit');
        setSelectedTermId(null);
    };


    // Render results header with selected terms
    const renderResultsHeader = (): JSX.Element | null => {
        if (!testState.results) return null;

        const mappings = testState.results.mappings || [];
        const mappedTerms = mappings.filter(m => m.mapped);

        // Find selected terms (highest confidence)
        const selectedTerms = mappedTerms
            .filter(m => m.confidenceScore && m.confidenceScore >= testState.confidenceThreshold)
            .sort((a, b) => (b.confidenceScore || 0) - (a.confidenceScore || 0));

        return (<Box sx={{mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    {selectedTerms.length > 0 ? (<>
                        <Typography variant="subtitle2" color="text.secondary">
                            Selected Term{selectedTerms.length > 1 ? 's' : ''}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            {selectedTerms.map((term, index) => (
                                <Stack key={term.termId} direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="h6">
                                        {term.term}
                                    </Typography>
                                    <Chip
                                        label={`${((term.confidenceScore || 0) * 100).toFixed(0)}%`}
                                        size="small"
                                        color="success"
                                    />
                                    {index < selectedTerms.length - 1 && (
                                        <Typography variant="body1" sx={{mx: 1}}>•</Typography>)}
                                </Stack>))}
                        </Stack>
                    </>) : (<Typography variant="body1" color="text.secondary">
                        No term selected with sufficient confidence
                    </Typography>)}
                </Box>
                <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                    {testState.results.processingTimeMs && (<Typography variant="body2" color="text.secondary">
                        Processing: {(testState.results.processingTimeMs / 1000).toFixed(1)}s
                    </Typography>)}
                    {testState.results.modelUsed && (<Typography variant="body2" color="text.secondary">
                        Model: {testState.results.modelUsed}
                    </Typography>)}
                </Stack>
            </Stack>
        </Box>);
    };

    // Render highlighted report content
    const renderHighlightedContent = (): { __html: string } => {
        let highlightedText = testState.testContent;

        // Get phrases to highlight based on selected term or all mapped terms
        const termsToHighlight = selectedTermId ? testState.results?.mappings.filter(m => m.termId === selectedTermId) || [] : testState.results?.mappings.filter(m => m.mapped) || [];

        const phrasesToHighlight = termsToHighlight.flatMap(m => m.matchedPhrases || []);

        // Sort phrases by length (longest first) to avoid partial replacements
        const sortedPhrases = Array.from(new Set(phrasesToHighlight)).sort((a, b) => b.length - a.length);

        sortedPhrases.forEach(phrase => {
            const regex = new RegExp(`(${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
            highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
        });

        return {__html: highlightedText};
    };

    // Render terms as tokens
    const renderTermsAsTokens = (): JSX.Element | null => {
        if (!testState.results?.mappings) return null;

        const mappings = testState.results.mappings;

        return (<Box sx={{mt: 2}}>
            <Typography variant="subtitle2" sx={{mb: 1}}>
                Descriptor Terms
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{gap: 1}}>
                {mappings.map(mapping => (<Tooltip
                    key={mapping.termId}
                    title={<Box>
                        <Typography variant="caption" fontWeight="bold">
                            {mapping.definition}
                        </Typography>
                        {mapping.explanation && testState.includeExplanations && (
                            <Typography variant="caption" display="block" sx={{mt: 1}}>
                                {mapping.explanation}
                            </Typography>)}
                        {mapping.matchedPhrases && mapping.matchedPhrases.length > 0 && (
                            <Typography variant="caption" display="block" sx={{mt: 1}}>
                                Matched: {mapping.matchedPhrases.join(', ')}
                            </Typography>)}
                    </Box>}
                    arrow
                >
                    <Chip
                        label={<Stack direction="row" spacing={0.5} alignItems="center">
                            {mapping.mapped && <CheckIcon sx={{fontSize: 16}}/>}
                            <span>{mapping.term}</span>
                            {mapping.mapped && mapping.confidenceScore && (
                                <span>({(mapping.confidenceScore * 100).toFixed(0)}%)</span>)}
                        </Stack>}
                        variant={mapping.mapped ? 'filled' : 'outlined'}
                        color={mapping.mapped ? 'success' : 'default'}
                        onClick={(): void => setSelectedTermId(mapping.termId === selectedTermId ? null : mapping.termId)}
                        sx={{
                            cursor: 'pointer', opacity: mapping.mapped ? 1 : 0.6, '&:hover': {
                                opacity: 1, bgcolor: mapping.mapped ? 'success.dark' : 'action.hover'
                            }, ...(selectedTermId === mapping.termId && {
                                borderWidth: 2, borderColor: 'primary.main'
                            })
                        }}
                    />
                </Tooltip>))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
                Click a term to highlight its matched phrases in the report
            </Typography>
        </Box>);
    };

    // Render results section
    const renderResults = (): JSX.Element | null => {
        if (!testState.results) return null;

        const mappings = testState.results.mappings || [];

        return (<Box sx={{mt: 3}}>
            <Divider sx={{mb: 2}}/>
            <Typography variant="h6" sx={{mb: 2}}>Results</Typography>

            {/* Error from LLM if any */}
            {testState.results.error && (<Alert severity="error" sx={{mb: 2}}>
                <Typography variant="body2">
                    {testState.results.error.message}
                </Typography>
                {testState.results.error.details && (<Typography variant="caption" component="div" sx={{mt: 1}}>
                    {testState.results.error.details}
                </Typography>)}
            </Alert>)}

            {/* Only show results if we have mappings */}
            {mappings.length > 0 ? (<>
                {/* Results Header */}
                {renderResultsHeader()}

                {/* Terms as Tokens */}
                {renderTermsAsTokens()}
            </>) : (/* No mappings available */
                !testState.results.error && (<Typography color="text.secondary">
                    No mapping results available. The descriptor may not have any terms defined.
                </Typography>))}
        </Box>);
    };

    return (<Paper elevation={1} sx={{p: 2, mt: 2, position: 'relative'}}>
        {/* Unsaved changes warning */}
        {hasUnsavedChanges && (
            <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    You have unsaved changes. Please save the descriptor before testing to ensure you're testing the latest version.
                </Typography>
            </Alert>
        )}

        {/* Loading overlay */}
        {testState.isLoading && (<Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1
        }}>
            <Stack alignItems="center" spacing={2}>
                <CircularProgress/>
                <Typography variant="body2" color="text.secondary">
                    Analyzing report content...
                </Typography>
            </Stack>
        </Box>)}

        {/* Configuration Row */}
        <Stack direction="row" spacing={2} sx={{mb: 2}}>
            <Typography variant="h6" sx={{mb: 2}}>
                Test Descriptor Mapping
            </Typography>
            <TextField
                label="Confidence Threshold"
                type="number"
                size="small"
                value={testState.confidenceThreshold}
                onChange={handleThresholdChange}
                disabled={isDisabled}
                inputProps={{
                    min: 0, max: 1, step: 0.1
                }}
                sx={{width: 150}}
            />

            <FormControlLabel
                control={<Checkbox
                    checked={testState.includeExplanations}
                    onChange={handleExplanationsChange}
                    disabled={isDisabled}
                />}
                label="Include Explanations"
            />
        </Stack>

        <Divider sx={{mb: 2}}/>

        {/* Report Content Input */}
        <Box sx={{mb: 2}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                <Typography variant="subtitle2">
                    Report Content
                </Typography>
                {testState.results && (<IconButton
                    size="small"
                    onClick={(): void => setViewMode(viewMode === 'edit' ? 'highlight' : 'edit')}
                    title={viewMode === 'edit' ? 'View highlights' : 'Edit content'}
                >
                    {viewMode === 'edit' ? <VisibilityIcon/> : <EditIcon/>}
                </IconButton>)}
            </Stack>

            {viewMode === 'edit' ? (<TextField
                multiline
                rows={6}
                fullWidth
                placeholder="Paste incident report text here..."
                value={testState.testContent}
                onChange={handleContentChange}
                disabled={isDisabled}
                error={!!testState.error}
                helperText={testState.error}
                sx={{
                    '& textarea': {
                        fontSize: '0.875rem', fontFamily: 'monospace'
                    }
                }}
            />) : (<Box
                sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: 150,
                    maxHeight: 300,
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    '& mark': {
                        backgroundColor: 'warning.light', padding: '2px 4px', borderRadius: '3px', fontWeight: 500
                    }
                }}
                dangerouslySetInnerHTML={renderHighlightedContent()}
            />)}
        </Box>

        {/* Action Buttons */}
        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
            <Button
                variant="outlined"
                onClick={handleClear}
                disabled={isDisabled || !testState.testContent}
                startIcon={<ClearIcon/>}
            >
                Clear
            </Button>
            <Button
                variant="contained"
                onClick={handleTest}
                disabled={isDisabled || !testState.testContent}
                startIcon={<PlayCircleOutlineIcon/>}
            >
                Test Mapping
            </Button>
        </Box>

        {/* Results Section */}
        {renderResults()}
    </Paper>);
};